/* eslint-disable no-unused-vars */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Button, Flex, Text, useDisclosure } from '@chakra-ui/react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormValidationToast } from '../../../services/actionToast';
import { FormButton } from '../../buttons/FormButton';
import { ReturnButton } from '../../buttons/ReturnButton';
import { getFetchData, useFetch } from '../../../hooks/useFetch';
import { SelectInputForm } from '../../inputs/forms/SelectInputForm';
import AlertModal from '../../AlertModal';
import { PageTitle } from '../../PageTitle';
import { FormBox } from '../../formBox';
import { FormHeading } from '../../formHeading';
import { getFormattedLabel } from '../../../utils/FormatFunctions';

interface AssessmentsFormProps {
  defaultValues?: any;
}

interface FormInputsProps {
  name: string;
  value: any;
  placeholder?: string;
  label: string;
  yup: any;
}

export default function AssessmentsForm({ defaultValues = {} }: AssessmentsFormProps) {
  const validationToast = useFormValidationToast();
  const baseUrl = '/v1/admin/ratings';
  const { requestApi: reqApiTeams, error: errorTeams } = useFetch(baseUrl, {
    actionFeedback: false,
  });
  const {
    requestApi: reqApiTopics,
    data: dataTopics,
    loading: loadingTopics,
  } = useFetch('/v1/admin/rating/topics');
  const { data: optionsTopics, pagination: paginationTopics } = getFetchData(dataTopics);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { itemId } = useParams();
  const navigate = useNavigate();
  const yupschema: any = {};
  const defaultValuesSchema: any = {};

  const formInputs: FormInputsProps[] = [
    {
      name: 'rating_topics',
      value: [...(defaultValues?.rating_topics?.map((item) => getFormattedLabel(item)) ?? [])],
      placeholder: 'Tópicos da turma',
      label: 'Tópicos',
      yup: Yup.array().min(1, 'Este campo é obrigatório!'),
    },
  ];

  formInputs.forEach((object) => {
    yupschema[object.name] = object.yup;
    defaultValuesSchema[object.name] = object.value;
  });
  const schema = Yup.object().shape(yupschema);

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValuesSchema,
  });

  const topics = formInputs.find((field) => field.name === 'rating_topics');
  const onSubmit = async (values: any) => {
    const params = {
      ...values,
      rating_topics: values.rating_topics.map((item) => ({ id: item.value })),
    };
    await reqApiTeams(
      defaultValues.id ? 'put' : 'post',
      params,
      defaultValues.id ? `${baseUrl}/${itemId}` : null
    );
    onOpen();
  };

  useEffect(() => {
    if (defaultValues.name) {
      reset(defaultValuesSchema);
    }
  }, [defaultValues]);

  return (
    <FormBox>
      <Flex>
        <ReturnButton previousPage="assessments/list" mb={5} />
        <PageTitle>{defaultValues.name ? 'Editar' : 'Criar'} Avaliação</PageTitle>
      </Flex>

      <AlertModal
        alertMsg={
          <Text textAlign="center" fontWeight="bold" fontSize="25px">
            {!errorTeams
              ? `Avaliação ${defaultValues.name ? 'editada' : 'criada'} com sucesso!`
              : `Erro ao ${defaultValues.name ? 'editar' : 'criar'} a Avaliação.`}
          </Text>
        }
        msgStatus={errorTeams ? 'error' : 'success'}
        closeOnOverlayClick={!!errorTeams}
        useSecondaryActions
        isOpenSecondary={isOpen}
        onCloseSecondary={onClose}
        ConfirmButton={
          <Button
            w="250px"
            boxShadow="0px 5px 10px #00000065"
            borderRadius="full"
            onClick={() => navigate('/assessments/list')}
          >
            Retornar para a listagem
          </Button>
        }
      />

      <form noValidate onSubmit={handleSubmit(onSubmit, validationToast)}>
        <FormHeading>Escolha os tópicos da avaliação</FormHeading>

        <Flex gap={5}>
          <Box flex={1}>
            <SelectInputForm
              name={topics?.name}
              label={topics?.label}
              options={[...(optionsTopics?.map((item) => getFormattedLabel(item)) ?? [])]}
              isLoading={loadingTopics}
              pagination={paginationTopics}
              getOptions={(filters) => reqApiTopics('get', { params: { ...filters } })}
              isMulti
              isRequired
              placeholder={topics?.placeholder}
              control={control}
              errors={errors}
            />
          </Box>
        </Flex>

        <Flex mr="auto">
          <FormButton
            mt={4}
            w="100px"
            isLoading={isSubmitting}
            type="submit"
            textTransform="uppercase"
          >
            Salvar
          </FormButton>
        </Flex>
      </form>
    </FormBox>
  );
}
